import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { Banknote, CornerDownLeft, Laptop, Search, UserRound, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import {
  FUND_REQUEST_STATUS_LABELS,
  PERMISSIONS,
  type AssetDto,
  type FundRequestDto,
  type Permission,
} from '@hrms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/use-auth';
import { DialogOverlay } from '@/components/ui/dialog';
import {
  employeeKeys,
  employeesApi,
  type EmployeeOption,
} from '@/features/employees/employees-api';
import { NAV_SECTIONS, visibleSections } from './nav-items';
import { useAssetMatches, useFundRequestMatches } from './use-command-sources';

export interface Command {
  /** Stable key, and the suffix of the row's DOM id. */
  id: string;
  label: string;
  /** Heading this belongs under, and matched against. */
  group: string;
  to: string;
  icon: LucideIcon;
  /** Secondary text, shown to the right of the label. */
  hint?: string;
  /** Also searched, below the label. An employee number, for instance. */
  keywords?: string;
  kind: 'nav' | 'employee' | 'asset' | 'fund-request';
}

/**
 * How well `query` matches a command, lower being better; -1 is no match.
 *
 * Ranked rather than merely filtered, because the substring alone puts "Leave
 * Entitlements" above "Leave" for the query "leave" — the shorter, more
 * general destination is almost always the one wanted. Word starts beat
 * mid-word hits for the same reason: "day" should find "Rest Day Work" before
 * "Holidays".
 */
export function scoreCommand(query: string, command: Command): number {
  const needle = query.trim().toLowerCase();
  if (!needle) return 0;

  const label = command.label.toLowerCase();
  if (label === needle) return 0;
  if (label.startsWith(needle)) return 1;
  // Names are stored surname-first ("Reyes, Ana Marie"), so a given name is a
  // word start rather than a prefix — this is the rule that finds people.
  if (label.split(/[\s/,]+/).some((word) => word.startsWith(needle))) return 2;
  if (label.includes(needle)) return 3;
  if (command.keywords?.toLowerCase().includes(needle)) return 4;
  // Matching the group is worth something — somebody typing "finance" wants
  // that whole section offered, not nothing.
  if (command.group.toLowerCase().includes(needle)) return 5;

  return -1;
}

/** The destinations this user can reach, in nav order. */
export function navCommands(canAny: (...permissions: Permission[]) => boolean): Command[] {
  return visibleSections(NAV_SECTIONS, canAny).flatMap((section) =>
    section.items.map((item) => ({
      id: item.to,
      label: item.label,
      group: section.title,
      to: item.to,
      icon: item.icon,
      kind: 'nav' as const,
    })),
  );
}

/**
 * Assets, from the register's search.
 *
 * The destination is the register filtered to the tag rather than a detail
 * page, because assets do not have one — the list is where every action on an
 * asset lives, and it reads `search` straight off the URL.
 */
export function assetCommands(assets: AssetDto[]): Command[] {
  return assets.map((asset) => ({
    id: `asset-${asset.id}`,
    label: `${asset.assetTag} · ${asset.name}`,
    group: 'Assets',
    to: `/assets?search=${encodeURIComponent(asset.assetTag)}`,
    icon: Laptop,
    hint: asset.holderName ?? 'Unassigned',
    keywords: [asset.serialNumber, asset.brand, asset.categoryName].filter(Boolean).join(' '),
    kind: 'asset' as const,
  }));
}

/** Fund requests from the queues this person can already see. */
export function fundRequestCommands(requests: FundRequestDto[]): Command[] {
  return requests.map((request) => ({
    id: `fund-request-${request.id}`,
    label: `${request.requestNo} · ${request.purpose}`,
    group: 'Fund requests',
    to: `/finance/fund-requests?request=${encodeURIComponent(request.requestNo)}`,
    icon: Banknote,
    hint: FUND_REQUEST_STATUS_LABELS[request.status] ?? request.status,
    keywords: request.employeeName,
    kind: 'fund-request' as const,
  }));
}

/** People, from the same list the "pick a person" selects use. */
export function employeeCommands(options: EmployeeOption[]): Command[] {
  return options.map((option) => ({
    id: `employee-${option.id}`,
    label: option.fullName,
    group: 'Employees',
    to: `/employees/${option.id}`,
    icon: UserRound,
    hint: option.departmentName || undefined,
    keywords: option.employeeNo,
    kind: 'employee' as const,
  }));
}

/** Best matches first, dropping what does not match at all. */
function rank(commands: Command[], query: string): Command[] {
  return (
    commands
      .map((command, index) => ({ command, score: scoreCommand(query, command), index }))
      .filter((entry) => entry.score >= 0)
      // `index` keeps the sort stable: equal scores stay in source order, which
      // for the nav is the order of the sidebar and for people is by surname.
      .sort((a, b) => a.score - b.score || a.index - b.index)
      .map((entry) => entry.command)
  );
}

/** Below this, a name search matches most of the company. */
const MIN_PEOPLE_QUERY = 2;
const MAX_PEOPLE_RESULTS = 6;
/** Records are the least likely of the three to be what was meant. */
const MAX_RECORD_RESULTS = 6;

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Go-anywhere search over the navigation and the staff list.
 *
 * Fifty-odd destinations behind eight collapsed groups is two clicks and a scan
 * even when you know exactly where you are going. Typing three letters is not.
 *
 * Built on the dialog already here rather than a combobox library: this renders
 * on every page, so it sits in the entry chunk, and a dependency there is paid
 * for by every first load — including the phones this was meant to help.
 *
 * Both lists are permission-filtered, so a destination or a person somebody
 * cannot reach is not offered. That is a courtesy, not a control: every route
 * is guarded and every endpoint re-checks.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { canAny } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // The same grants the endpoint enforces. Without them the request is a 403 on
  // every open, and the palette would advertise a search that cannot work.
  const canSearchPeople = canAny(
    PERMISSIONS.EMPLOYEE_READ_ALL,
    PERMISSIONS.EMPLOYEE_READ_TEAM,
    PERMISSIONS.USER_MANAGE,
  );

  /*
   * The whole active roster, once, rather than a request per keystroke.
   *
   * It is the list every "pick a person" select already loads, so it is usually
   * warm in the cache by the time the palette is first opened, and matching in
   * the browser makes typing instant. `enabled` on `open` keeps it off the
   * critical path of the first page load — nobody pays for this until they
   * actually reach for the palette.
   */
  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: employeeKeys.options(),
    queryFn: employeesApi.options,
    enabled: open && canSearchPeople,
    staleTime: 5 * 60_000,
    // A palette that cannot reach the roster still navigates; it must not
    // become a red screen over the top of the app.
    throwOnError: false,
  });

  const { assets, isSearching: assetsSearching } = useAssetMatches({ open, query, canAny });
  const { requests, isSearching: requestsSearching } = useFundRequestMatches({
    open,
    query,
    canAny,
  });

  const nav = useMemo(() => navCommands(canAny), [canAny]);

  // Gated on the permission again, not just on `enabled` above: a disabled
  // query still returns whatever is already in the cache, so somebody who may
  // not read the roster could otherwise be offered names another session left
  // behind.
  const staff = useMemo(
    () => (canSearchPeople ? employeeCommands(people) : []),
    [people, canSearchPeople],
  );

  const records = useMemo(
    () => [...fundRequestCommands(requests), ...assetCommands(assets)],
    [requests, assets],
  );

  const { results, navCount } = useMemo(() => {
    const navResults = rank(nav, query);
    const searching = query.trim().length >= MIN_PEOPLE_QUERY;

    const peopleResults = searching ? rank(staff, query).slice(0, MAX_PEOPLE_RESULTS) : [];

    /*
     * Records after people, and capped the same way.
     *
     * The asset side is already narrowed by the server, but it still goes
     * through `rank` so a tag typed in full outranks a brand that merely
     * contains the letters — and so the two sources interleave by relevance
     * rather than by which request happened to answer first.
     */
    const recordResults = searching ? rank(records, query).slice(0, MAX_RECORD_RESULTS) : [];

    // Destinations first, then people: the nav is a closed set somebody is
    // navigating deliberately, while a name match is a guess at what they meant.
    return {
      results: [...navResults, ...peopleResults, ...recordResults],
      navCount: navResults.length,
    };
  }, [nav, staff, records, query]);

  // A stale highlight after typing would send Enter somewhere unrelated.
  useEffect(() => setActive(0), [query]);

  // Reopening should not resume somebody else's half-typed search.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, results]);

  const go = (command: Command | undefined) => {
    if (!command) return;
    onOpenChange(false);
    navigate(command.to);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(results.length - 1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[active]);
    }
  };

  const searching = query.trim().length > 0;
  // Any source still in flight. Assets are the slow one — a request per settled
  // query rather than a cached list — so this is mostly about them.
  const lookingUp =
    searching && ((canSearchPeople && peopleLoading) || assetsSearching || requestsSearching);

  /** The heading to print above `index`, if any. */
  const headingAt = (index: number): string | null => {
    const command = results[index];
    if (!command) return null;

    // While browsing, the nav's own sections. Once a search reorders the list by
    // relevance those blocks no longer hold, so the divisions that survive are
    // the ones between kinds of thing: destinations, people, records.
    if (!searching) {
      return command.group !== results[index - 1]?.group ? command.group : null;
    }

    if (index === 0) {
      // A list that is only destinations needs no heading saying so; one that
      // starts with a record needs its own, or the first group goes unnamed
      // whenever nothing in the nav happens to match.
      if (command.kind === 'nav') return results.length > navCount ? 'Go to' : null;
      return command.group;
    }

    // Nav results are one undifferentiated block under "Go to"; past them, a
    // heading whenever the group changes, which separates Employees from Fund
    // requests from Assets without naming them here.
    if (index < navCount) return null;

    return command.group !== results[index - 1]?.group ? command.group : null;
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          // Near the top rather than centred: the list grows downwards, and a
          // vertically centred palette jumps as results come and go.
          className={cn(
            'fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2',
            'overflow-hidden rounded-xl border bg-popover text-popover-foreground shadow-lg',
            // `command-in` keeps the `-translate-x-1/2` centring through the
            // fade — a generic `animate-in` would drop the translate and the
            // palette would slide in from the left edge.
            'data-[state=open]:command-in',
          )}
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">Search the app</DialogPrimitive.Title>

          <div className="flex items-center gap-2.5 border-b px-3.5">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={canSearchPeople ? 'Go to, or find someone or something…' : 'Go to…'}
              aria-label="Search destinations and people"
              // A combobox, so the number of results and the highlighted one are
              // announced instead of silently changing under the cursor.
              role="combobox"
              aria-expanded
              aria-controls="command-results"
              aria-activedescendant={results[active] ? `command-${results[active].id}` : undefined}
              className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div
            id="command-results"
            ref={listRef}
            role="listbox"
            aria-label="Destinations and people"
            className="max-h-[min(24rem,60vh)] overflow-y-auto p-1.5"
          >
            {results.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                {lookingUp ? 'Looking…' : `Nothing matches “${query.trim()}”.`}
              </p>
            ) : (
              results.map((command, index) => {
                const isActive = index === active;
                const heading = headingAt(index);
                // A person's department, or — once a search has broken the
                // sections apart — the group a destination came from.
                const meta =
                  command.hint ?? (searching && command.kind === 'nav' ? command.group : undefined);

                return (
                  <div key={command.id}>
                    {heading && (
                      <p className="px-2.5 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {heading}
                      </p>
                    )}

                    <div
                      id={`command-${command.id}`}
                      role="option"
                      aria-selected={isActive}
                      data-active={isActive}
                      // Pointer users get the same highlight the keyboard moves,
                      // so Enter always goes where the eye is.
                      onMouseMove={() => setActive(index)}
                      onClick={() => go(command)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm',
                        isActive ? 'bg-accent text-accent-foreground' : 'text-foreground/80',
                      )}
                    >
                      <command.icon
                        className="size-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <span className="truncate">{command.label}</span>

                      {meta && (
                        <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                          {meta}
                        </span>
                      )}

                      {isActive && (
                        <CornerDownLeft
                          className={cn(
                            'size-3.5 shrink-0 text-muted-foreground',
                            meta ? 'ml-1.5' : 'ml-auto',
                          )}
                          aria-hidden="true"
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Only worth saying while the roster is still on the wire and the
                nav has already answered — otherwise it is noise under results
                that are complete. */}
            {lookingUp && results.length > 0 && (
              <p className="px-2.5 py-2 text-xs text-muted-foreground">Still looking…</p>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Palette state, plus the shortcut that opens it from anywhere.
 *
 * The listener sits on the document rather than on a field, because the whole
 * point is reaching it without first putting the cursor somewhere. `preventDefault`
 * matters on Firefox, where Ctrl+K is the address-bar search.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k' || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      setOpen((current) => !current);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return { open, setOpen };
}

/** True on a Mac, where the shortcut is Command rather than Control. */
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);
}

/**
 * The topbar's way in.
 *
 * A shortcut nobody is told about is a shortcut nobody uses, so the key is
 * printed on the control rather than left to be discovered.
 */
export function CommandPaletteTrigger({ onOpen }: { onOpen: () => void }) {
  const [isMac, setIsMac] = useState(false);

  // After mount: the platform is not knowable while rendering on the server,
  // and reading it during render would make this component non-deterministic.
  useEffect(() => setIsMac(isApplePlatform()), []);

  return (
    <button
      type="button"
      onClick={onOpen}
      // Below `sm` it collapses to the icon: a phone has no keyboard shortcut
      // to advertise, and the topbar has no room for the hint.
      className={cn(
        'flex h-9 items-center gap-2 rounded-md text-sm text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
        'w-9 justify-center sm:w-56 sm:justify-start sm:border sm:border-input sm:px-3',
      )}
    >
      <Search className="size-4 shrink-0" aria-hidden="true" />
      <span className="hidden sm:inline">Search</span>
      <kbd className="ml-auto hidden rounded border bg-muted px-1.5 py-0.5 font-sans text-[11px] font-medium sm:inline">
        {isMac ? '⌘' : 'Ctrl '}K
      </kbd>
      <span className="sr-only">Search the app</span>
    </button>
  );
}
